import React, { useCallback } from "react";
import type { ConsortiumInfoRow } from "../../../../../api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../app/store";
import type { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addConsortiumRow,
  deleteConsortiumRow,
  selectFMR1DraftConsortiumRows,
  updateConsortiumRow,
} from "../../../logic/frm1Slice";
import { FMRTable, type FMRTableColumn } from "../common/FMRTable";
import { generateRowId } from "../common/utils";

const buildEmptyRow = (): ConsortiumInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

export const ConsortiumMembersTable: React.FC = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftConsortiumRows);

  const handleAddRow = useCallback((_rowIndex: number) => {
    const newRow = buildEmptyRow();
    dispatch(addConsortiumRow({ row: newRow }));
  }, [dispatch]);

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      const row = rows[rowIndex];
      if (!row) return;
      dispatch(deleteConsortiumRow({ id: row.row_id }));
    },
    [dispatch, rows],
  );

  const handleChange = useCallback(
    (rowIndex: number, value: unknown, field: keyof ConsortiumInfoRow) => {
      const row = rows[rowIndex];
      dispatch(updateConsortiumRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, rows]
  );

  const columns: FMRTableColumn<ConsortiumInfoRow>[] = [
    {
      headerName: "Bank name **",
      headerClassName:
        "bg-[#E5E7EB] font-sans font-medium text-sm leading-5 text-[#000000] align-middle border-b border-l border-[#E5E5E5]",
      headerStyle: { width: 402, minWidth: 85, height: 44 },
      field: "bank_name",
      type: "text" as ColumnType,
      isEditable: true,
      textCellProps: { truncateChars: 50, numberOfRows: 2 },
      getValue: (row: ConsortiumInfoRow) => row.bank_name ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "bank_name"),
    },
    {
      headerName: "Others (Please specify)",
      headerClassName:
        "bg-[#E5E7EB] font-sans font-medium text-sm leading-5 text-[#000000] align-middle border-b border-l border-[#E5E5E5]",
      headerStyle: { width: 208, minWidth: 85, height: 36 },
      field: "others_specify",
      type: "text" as ColumnType,
      isEditable: true,
      textCellProps: { truncateChars: 23, numberOfRows: 2 },
      getValue: (row: ConsortiumInfoRow) => row.others_specify ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "others_specify"),
    },
    {
      headerName: "Consortium/MBA Member Amount **",
      headerClassName:
        "bg-[#E5E7EB] font-sans font-medium text-sm leading-5 text-[#000000] align-middle border-b border-l border-[#E5E5E5]",
      headerStyle: { width: 421, minWidth: 85, height: 36 },
      field: "consortium_amount",
      type: "number" as ColumnType,
      isEditable: true,
      getValue: (row: ConsortiumInfoRow) => (row.consortium_amount as number | null | undefined) ?? null,
      onChange: (rowIndex: number, value: number | null) => handleChange(rowIndex, value, "consortium_amount"),
    },
  ];

  return (
    <FMRTable
      columns={columns}
      rows={rows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      emptyStateContainerClassName="w-full"
      rounded={false}
      disableFocusWithinStyles
      disableActiveCellStyles
    />
  );
};

ConsortiumMembersTable.displayName = "ConsortiumMembersTable";
